// src/components/home/ReportModal.tsx
import React, { useState } from 'react';
import { FaTimes, FaFlag } from 'react-icons/fa';
import { ReportReason } from '../../services/reportService';
 
interface ReportModalProps {
  darkMode: boolean;
  isOpen: boolean;
  postId: string;
  onClose: () => void;
  onSubmit: (reason: ReportReason, description: string) => Promise<void>;
}
 
export const ReportModal: React.FC<ReportModalProps> = ({
  darkMode, isOpen, onClose, onSubmit,
}) => {
  const [selectedReason, setSelectedReason] = useState<ReportReason>('spam');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
 
  const reasons: { value: ReportReason; label: string; description: string }[] = [
    { value: 'spam', label: 'Spam', description: 'Repetitive or promotional content' },
    { value: 'harassment', label: 'Harassment', description: 'Bullying or targeted attacks' },
    { value: 'inappropriate', label: 'Inappropriate', description: 'Offensive or inappropriate content' },
    { value: 'nsfw', label: 'NSFW', description: 'Adult or explicit content' },
    { value: 'misinformation', label: 'Misinformation', description: 'False or misleading information' },
    { value: 'other', label: 'Other', description: 'Other violations' },
  ];
 
  const handleSubmit = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);
    try {
      await onSubmit(selectedReason, description);
      setDescription('');
      setSelectedReason('spam');
      onClose();
    } catch (error) {
      console.error('Report submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
 
  if (!isOpen) return null;
 
  const bg = darkMode ? '#0f2424' : '#f0fafa';
  const border = darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)';
 
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,26,26,0.75)', backdropFilter: 'blur(8px)' }}>
      <div
        className="w-full max-w-lg p-6"
        style={{
          background: bg,
          border: `2px solid ${border}`,
          borderRadius: '16px',
          boxShadow: '6px 6px 0 rgba(13,26,26,0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ background: 'rgba(232,168,32,0.12)', border: '1.5px solid rgba(232,168,32,0.25)' }}>
              <FaFlag style={{ color: '#e8a820', fontSize: '14px' }} />
            </div>
            <h2 className="font-black text-base" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '3px', color: darkMode ? '#f0fafa' : '#0d1a1a' }}>
              REPORT POST
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg transition-all"
            style={{ color: darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.4)' }}>
            <FaTimes style={{ fontSize: '14px' }} />
          </button>
        </div>
 
        <p className="text-xs font-bold mb-4" style={{ color: darkMode ? 'rgba(126,206,206,0.45)' : 'rgba(13,26,26,0.5)', letterSpacing: '0.5px' }}>
          Help keep Onyx safe. Select a reason below.
        </p>
 
        {/* Reasons */}
        <div className="space-y-2 mb-4">
          {reasons.map(r => (
            <button
              key={r.value}
              onClick={() => setSelectedReason(r.value)}
              className="w-full text-left p-3 rounded-xl transition-all"
              style={{
                background: selectedReason === r.value ? 'rgba(232,168,32,0.1)' : darkMode ? '#0d1a1a' : '#ffffff',
                border: `1.5px solid ${selectedReason === r.value ? '#e8a820' : border}`,
                boxShadow: selectedReason === r.value ? '2px 2px 0 rgba(232,168,32,0.2)' : 'none',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-black" style={{ color: selectedReason === r.value ? '#e8a820' : (darkMode ? '#f0fafa' : '#0d1a1a'), letterSpacing: '0.5px' }}>
                    {r.label.toUpperCase()}
                  </div>
                  <div className="text-[10px] font-bold mt-0.5" style={{ color: darkMode ? 'rgba(126,206,206,0.35)' : 'rgba(13,26,26,0.4)' }}>
                    {r.description}
                  </div>
                </div>
                {selectedReason === r.value && (
                  <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: '#e8a820', border: '1.5px solid #0d1a1a' }}>
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#0d1a1a' }} />
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
 
        {/* Details */}
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Additional details (optional)"
          rows={3}
          className="w-full p-3 rounded-lg resize-none text-xs focus:outline-none transition-all font-medium"
          style={{
            background: darkMode ? '#0d1a1a' : '#ffffff',
            border: `1.5px solid ${border}`,
            color: darkMode ? '#f0fafa' : '#0d1a1a',
          }}
          onFocus={e => (e.target.style.borderColor = '#5ab8b8')}
          onBlur={e => (e.target.style.borderColor = border)}
        />
 
        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-black transition-all"
            style={{ color: darkMode ? 'rgba(126,206,206,0.45)' : 'rgba(13,26,26,0.45)', letterSpacing: '1px' }}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-5 py-2 rounded-lg text-xs font-black transition-all disabled:opacity-40"
            style={{
              background: '#e8a820',
              border: '2px solid #0d1a1a',
              boxShadow: '3px 3px 0 #0d1a1a',
              color: '#0d1a1a',
              letterSpacing: '1px',
            }}
            onMouseEnter={e => { if (!isSubmitting) { (e.currentTarget as HTMLButtonElement).style.transform = 'translate(1px,1px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '1px 1px 0 #0d1a1a'; } }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = ''; (e.currentTarget as HTMLButtonElement).style.boxShadow = '3px 3px 0 #0d1a1a'; }}
          >
            {isSubmitting ? 'REPORTING...' : 'SUBMIT REPORT'}
          </button>
        </div>
      </div>
    </div>
  );
};